import React from 'react'
import { CalculateAge } from '../utils/CalculateAge'
import { parseDateTime } from '../utils/FormateDateTime'

export const formatOrderSource = orderSource => {
  const parts = orderSource ? orderSource.split('_') : []
  const firstPart = parts?.[0]
    ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
    : ''

  return orderSource ? `${firstPart} ${parts?.[1] || ''}` : 'Out Patient'
}

const clickableValue = (value, fileNo, onPatientClick) => {
  if (!onPatientClick || !fileNo || !value || value === '-') return value || '-'

  return (
    <span
      className='text-blue-600 cursor-pointer hover:text-blue-800 hover:underline'
      onClick={() => onPatientClick(fileNo)}
      title='Click to view patient profile'
    >
      {value}
    </span>
  )
}

const detailsStack = rows => (
  <div className='min-w-[190px] space-y-0.5 text-[12px] leading-[1.05rem]'>
    {rows.map(row => (
      <div
        key={row.label}
        className={`grid grid-cols-[88px_auto] gap-6 px-1 py-0.5 ${
          row.highlight ? 'w-fit' : ''
        }`}
      >
        <span className='font-normal text-gray-500 whitespace-nowrap flex items-center justify-between'>
          <span>{row.label}</span>
          <span>=</span>
        </span>
        <span
          className={`font-semibold break-words whitespace-pre-line ${
            row.highlight ? 'text-primary' : 'text-gray-800'
          }`}
        >
          {row.value || '-'}
        </span>
      </div>
    ))}
  </div>
)

export const formatOrderDateParts = value => {
  const date = parseDateTime(value)

  return {
    date: date.isValid() ? date.format('DD MMM YYYY') : '-',
    time: date.isValid() ? date.format('hh:mm A') : '-'
  }
}

const getOrderDate = (rowData, dateField) => {
  const directDate =
    rowData?.[dateField] || rowData?.received_at || rowData?.created_at
  if (directDate) return directDate

  const firstHistoryItem = Object.values(rowData?.items_history || {})[0]
  return firstHistoryItem?.history?.[0]?.created_at
}

export const getBilledDate = rowData =>
  rowData?.billed_at ||
  rowData?.billing?.billed_at ||
  rowData?.billing_info?.billed_at ||
  rowData?.invoice?.billed_at ||
  rowData?.bill?.billed_at

const formatStatus = status =>
  String(status || '-')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const patientDemographicsColumn = (t, onPatientClick = null) => ({
  field: 'patient_demographics',
  header: t('Patient Demographics'),
  body: rowData => {
    const patient = rowData?.patient || rowData?.patient_info || {}
    const fileNo = patient?.file_no || patient?.file_number || '-'

    return detailsStack([
      {
        label: t('Name'),
        value: clickableValue(
          patient?.name || patient?.full_name || '-',
          fileNo,
          onPatientClick
        )
      },
      {
        label: t('File No'),
        value: clickableValue(fileNo, fileNo, onPatientClick)
      },
      {
        label: t('Age'),
        value:
          CalculateAge(patient?.dob || patient?.birth_date) ||
          patient?.age ||
          '-'
      },
      {
        label: t('Gender'),
        value: patient?.gender ? t(patient.gender) : '-'
      },
      {
        label: t('Type'),
        value: patient?.location || patient?.beneficiary_type || '-'
      },
      {
        label: t('Mobile'),
        value: patient?.phone || patient?.mobile || '-'
      },
      {
        label: t('Doc ID'),
        value: clickableValue(
          patient?.document_id || '-',
          fileNo,
          onPatientClick
        )
      }
    ])
  },
  style: { minWidth: '210px' }
})

export const orderDetailsColumn = (
  t,
  dateField = 'updated_at',
  options = {}
) => ({
  field: 'order_details',
  header: t('Order Details'),
  body: rowData => {
    const orderDateParts = formatOrderDateParts(
      getOrderDate(rowData, dateField)
    )
    const billedDateParts = formatOrderDateParts(getBilledDate(rowData))
    const status =
      rowData?.order_status ||
      rowData?.status ||
      rowData?.progess_status ||
      rowData?.progress_status

    const rows = [
      {
        label: t('Order No'),
        value: rowData?.order_no || rowData?.order_number || '-'
      },
      {
        label: t('Type'),
        value: formatOrderSource(rowData?.order_source)
      },
      {
        label: t('Date'),
        value: orderDateParts.date
      },
      {
        label: t('Time'),
        value: orderDateParts.time
      },
      {
        label: t('Billed Date'),
        value: billedDateParts.date
      },
      {
        label: t('Billed Time'),
        value: billedDateParts.time
      }
    ]

    if (options.showOrderStatus) {
      rows.push({
        label: t('Order Status'),
        value: status ? t(formatStatus(status)) : '-'
      })
    }

    rows.push({
      label: t('Practitioner'),
      value:
        rowData?.practitioner?.name ||
        rowData?.practitioner_info?.name ||
        rowData?.practitioner_name ||
        '-',
      highlight: true
    })

    return detailsStack(rows)
  },
  style: { minWidth: '210px' }
})
